import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import Layout from '@components/global/Layout';
import Container from '@components/global/Container';
import {
  getStoreByUid,
  getAllStoresDataForSetMyStore,
} from '@adapters/prismic/fetching/stores';
import { StoreHour } from '@adapters/prismic/fetching/stores/types';
import RichTextBlockComponent from '@components/cms/RichTextBlock';
import Breadcrumbs from '@components/global/Breadcrumbs';
import { AccordionToVerticalTabs } from '@components/ui/AccordionToVerticalTabs';
import AlertInlineMessage from '@components/ui/AlertInlineMessage';
import { StoreHeader } from '@components/stores/StoreHeader';
import StoreHours from '@components/stores/StoreHours';
import StoreInformation from '@components/stores/StoreInformation';
import Divider from '@components/ui/Divider';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import type { GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import getAllPromoTickerData from '@adapters/prismic/fetching/promos/getAllPromoTickerData';
import getBannerStripGlobalData from '@adapters/prismic/fetching/banner-strip-global/getBannerStripGlobalData';
import prismicLinkResolver from '@adapters/prismic/utils/linkResolver';
import { LocalBusinessJsonLd, NextSeo } from 'next-seo';
import { altLocale } from '@utils/locale';
import dayjs from 'dayjs';
import { MEC_TITLE } from '@utils/constants';
import { generateStoreTabsComponents, weeklyHours } from '../../../utils/store';
import { getPreviewRef } from '@adapters/prismic/utils/getPreviewRef';

import styles from './Store.module.scss';
import getPromoBarData from '@adapters/prismic/fetching/promos/getPromoBarData';
import { FEATURE_FLAG_PROMO_BAR_SERVER_SIDE } from '@config/feature-flag';

function abbreviateProvince(province: string) {
  switch (province) {
    case 'Newfoundland and Labrador':
      return 'NL';
      break;
    case 'Prince Edward Island':
      return 'PE';
      break;
    case 'Nova Scotia':
      return 'NS';
      break;
    case 'New Brunswick':
      return 'NB';
      break;
    case 'Québec':
      return 'QC';
      break;
    case 'Quebec':
      return 'QC';
      break;
    case 'Ontario':
      return 'ON';
      break;
    case 'Manitoba':
      return 'MB';
      break;
    case 'Saskatchewan':
      return 'SK';
      break;
    case 'Alberta':
      return 'AB';
      break;
    case 'British Columbia':
      return 'BC';
      break;
    case 'Yukon':
      return 'YT';
      break;
    case 'Northwest Territories':
      return 'NT';
      break;
    case 'Nunavut':
      return 'NU';
      break;
    default:
      return province;
      break;
  }
}

export const getStaticProps = async ({
  params,
  previewData,
  preview = false,
}: GetStaticPropsContext<{ locale: string; slug: string }>) => {
  const locale = params?.locale === 'fr' ? 'fr' : 'en';
  if (!params?.slug || !params.slug.length) {
    throw new Error(`No slug specified for store`);
  }

  if (!locale) {
    throw new Error('No locale specified for store');
  }

  const previewRef = getPreviewRef(preview, previewData);

  const [
    store,
    stores,
    promoMessages,
    serverSideTranslation,
    promoBarData,
    bannerStripData,
  ] = await Promise.allSettled([
    getStoreByUid(params?.slug, locale, preview ? previewRef : undefined),
    getAllStoresDataForSetMyStore(locale),
    !FEATURE_FLAG_PROMO_BAR_SERVER_SIDE &&
      locale &&
      getAllPromoTickerData(locale),
    serverSideTranslations(locale, ['common', 'stores', 'promotions']),
    FEATURE_FLAG_PROMO_BAR_SERVER_SIDE && locale && getPromoBarData(locale),
    getBannerStripGlobalData(locale, preview ? previewRef : undefined),
  ]).then((results) =>
    results.map((promise) => {
      if (
        promise.status === 'rejected' ||
        typeof promise.value === 'undefined'
      ) {
        console.error('promise rejected or returning undefined', promise);
        return null;
      }
      return promise.value;
    })
  );

  const storeData: unknown = store?.data ?? null;

  if (!store) {
    console.warn(`Store post with slug '${params?.slug}' not found`);
    return {
      notFound: true,
    };
  }

  // This function needs to be here as putting it in an utils file causes errros
  const transformMarkdownToAST = async (rawData: any) => {
    const tabs = [
      'welcome',
      'store_is_closed',
      'repairs_and_recycling',
      'green_building_features',
      'ski_services',
      'now_open',
      'community',
      'store_pickup',
      'events',
      'bike_services',
    ];

    const getPanelData = async (body: any) => {
      return body;
    };

    const data = await Promise.all(
      tabs.flatMap(async (tab) => {
        if (rawData[`${tab}_enabled`]) {
          const panelContent = await getPanelData(rawData[`${tab}_body2`]);
          return {
            tabContent: rawData[`${tab}_title`],
            panelContent,
          };
        }
        return null;
      })
    );

    const filterOutNullItems = data.filter((item) => item !== null);

    return filterOutNullItems;
  };

  // tranforms the markdown to abstrct syntax tree to be used in markdown components for the tabs
  const tabsData = (await transformMarkdownToAST(storeData)) ?? null;
  const canonicalUrl = process.env.BASE_URL + prismicLinkResolver(store);
  const langAltUrl =
    process.env.BASE_URL + prismicLinkResolver(store.alternate_languages[0]);
  const xDefault = locale === 'en' ? canonicalUrl : langAltUrl;

  return {
    props: {
      storeData,
      tabsData,
      stores,
      locale,
      canonicalUrl,
      langAltUrl,
      preview,
      xDefault,
      ...serverSideTranslation,
      ...(store.alternate_languages.length && {
        alternateLocaleUrl:
          prismicLinkResolver(store.alternate_languages[0]) ?? '',
      }),
      promoMessages,
      promoBarData,
      bannerStripData,
    },
  };
};

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

export default function StorePage({
  storeData,
  tabsData,
  canonicalUrl,
  langAltUrl,
  locale,
  xDefault,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const { t } = useTranslation('stores');

  const {
    store_name,
    hero_image,
    notice,
    store_hours,
    store_hour_exceptions,
    open_to_public,
    description,
    phone,
    address_line_1,
    address_line_2,
    address_city,
    address_province,
    address_postalcode,
    geo,
  } = storeData as any;

  const { url, alt, dimensions } = hero_image;
  const { height, width } = dimensions;
  const { latitude, longitude } = geo;
  const { text } = notice[0] || {};

  const tabs = generateStoreTabsComponents(tabsData);

  const orderedWeek: StoreHour[] = weeklyHours(
    store_hours,
    store_hour_exceptions
  );

  const seoTitle = t('stores.seo.title', {
    store: store_name,
    province: abbreviateProvince(address_province),
  });
  const breadcrumbs = [{ name: t('stores.store'), path: '/stores' }];

  return (
    <>
      <NextSeo
        title={`${store_name} ${MEC_TITLE}`}
        description={description}
        canonical={canonicalUrl}
        languageAlternates={[
          { hrefLang: altLocale(locale), href: langAltUrl },
          { hrefLang: String(locale), href: canonicalUrl },
          { hrefLang: 'x-default', href: xDefault },
        ]}
        openGraph={{
          title: seoTitle,
          locale,
        }}
      />
      <LocalBusinessJsonLd
        type="Store"
        id={canonicalUrl}
        url={canonicalUrl}
        name={`MEC ${store_name}`}
        description={description}
        telephone={phone}
        images={[url]}
        openingHours={[
          ...store_hours?.map((item: any) => ({
            opens: item.open,
            closes: item.close,
            dayOfWeek: [item.day],
          })),

          ...store_hour_exceptions.map((item: any) => ({
            opens: item.open,
            closes: item.close,
            validFrom: item.date,
            validThrough: item.date,
            dayOfWeek: dayjs(item.date).format('dddd'),
          })),
        ]}
        address={{
          streetAddress: address_line_1,
          addressLocality: address_city,
          addressRegion: address_province,
          postalCode: address_postalcode,
          addressCountry: 'Canada',
        }}
        geo={{
          latitude: String(latitude),
          longitude: String(longitude),
        }}
      />
      <Container>
        <Breadcrumbs name={store_name} breadcrumbs={breadcrumbs} />
        <div className={styles.row}>
          <div className={styles.left}>
            <StoreInformation
              {...{
                store_name,
                hero_image,
                notice,
                store_hours,
                store_hour_exceptions,
                open_to_public,
                description,
                phone,
                address_line_1,
                address_line_2,
                address_city,
                address_province,
                address_postalcode,
                geo,
              }}
            />
          </div>
          <div className={styles.right}>
            {url && (
              <div className={styles.heroImage}>
                <Image
                  src={url}
                  alt={alt || `${store_name} store`}
                  height={height}
                  width={width}
                  sizes="(min-width: 720px) 912px, 670px"
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                  }}
                />
              </div>
            )}

            {!!text && (
              <AlertInlineMessage messageType="WARNING">
                <RichTextBlockComponent
                  text={notice}
                  classNames={styles.storeAlertText}
                />
              </AlertInlineMessage>
            )}

            {open_to_public && <StoreHeader storeHours={orderedWeek} />}

            <StoreHours week={orderedWeek} exceptions={store_hour_exceptions} />
          </div>
        </div>

        <Divider className={styles.divider} />
        <AccordionToVerticalTabs items={tabs} />
      </Container>
    </>
  );
}

StorePage.Layout = Layout;
